/**
 * LinkRegistryModal — link a legacy CRM study to its eReg registry record
 * (source-of-truth plan §5.1 / §7.3). Search the tenant network's registry
 * via the CRM proxy, pick, confirm. Relinking an already-linked study (§6-E4)
 * surfaces the backend's confirm requirement.
 */
import { useState } from 'react';
import { CheckCircle2, Link2, Loader2 } from 'lucide-react';
import api from '../../../services/api';
import StudyPicker, { RegistryStudy } from '../../../ui/StudyPicker';
import { Z } from '../ui';

export default function LinkRegistryModal({
  studyId,
  studyLabel,
  onClose,
  onLinked,
}: {
  studyId: string;
  studyLabel: string;
  onClose: () => void;
  onLinked: () => void;
}) {
  const [selected, setSelected] = useState<RegistryStudy | null>(null);
  const [saving, setSaving] = useState(false);
  const [needsConfirm, setNeedsConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const link = async (confirm = false) => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/crm/studies/${studyId}/link-registry`, {
        protocol_id: selected.id,
        ...(confirm ? { confirm: true } : {}),
      });
      onLinked();
    } catch (err: any) {
      if (err?.response?.data?.code === 'RELINK_CONFIRM_REQUIRED') {
        setNeedsConfirm(true);
        setError(err.response.data.error);
      } else {
        setError(err?.response?.data?.error || 'Failed to link the study');
      }
      setSaving(false);
    }
  };

  return (
    <div className={`fixed inset-0 ${Z.modal} flex items-start justify-center overflow-y-auto bg-black/50 p-4`} onClick={onClose}>
      <div className="my-auto w-full max-w-md rounded-xl bg-white p-5 shadow-xl dark:bg-gray-800" onClick={(e) => e.stopPropagation()}>
        <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <Link2 className="h-4 w-4 text-primary-600 dark:text-primary-400" />
          Link to eReg
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Pick the eReg study record for <span className="font-medium text-gray-700 dark:text-gray-200">{studyLabel}</span>.
          Its current protocol/ICF become this study’s document source.
        </p>
        <div className="mt-4">
          {selected ? (
            <div className="flex items-start gap-2.5 rounded-lg border border-primary-200 bg-primary-50 px-3 py-2 dark:border-primary-800 dark:bg-primary-900/20">
              <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{selected.protocol_number}</p>
                <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                  {[selected.study_nickname, selected.sponsor_name, selected.site?.name].filter(Boolean).join(' · ') || '—'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => { setSelected(null); setNeedsConfirm(false); setError(null); }}
                className="inline-flex min-h-[44px] items-center rounded-lg px-2 py-1 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-300 dark:hover:bg-primary-900/40 sm:min-h-0"
              >
                Change
              </button>
            </div>
          ) : (
            <StudyPicker
              searchPath="/api/crm/registry/studies"
              onSelect={(s) => { setSelected(s); setNeedsConfirm(false); setError(null); }}
              allowCreate={false}
              placeholder="Search eReg studies by protocol number, name, or sponsor…"
              autoFocus
            />
          )}
        </div>
        {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="inline-flex min-h-[44px] items-center rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => link(needsConfirm)}
            disabled={saving || !selected}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 sm:min-h-0"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {needsConfirm ? 'Relink anyway' : 'Link study'}
          </button>
        </div>
      </div>
    </div>
  );
}
